import { FormEvent, FunctionComponent, useState } from 'react';
import Button from '@material-ui/core/Button';
import { CircularProgress } from '@material-ui/core';
import { TextField } from '@flos/react-components';
import { baseStyles } from '../../theme/base.styles';
import { useGlobalDispatch, useGlobalState } from '../../common/context/AppContext';
import GridWrapper from '../../components/shared/grid-wrapper/GridWrapper';
import { FLOW, IAppProps, ITextFieldData, REDUCER_KEY } from '../../common/interface/IAppProvider';
import { sendEmail } from '../../common/async/AsyncCalls';
import RenderHtml from '../../components/shared/render-html/RenderHtml';
import { str2float } from '../../common/utils/helpers/GenericHelpers';
import { track, errorTypes } from 'c9-tracking';
import { useStyles } from './CallMeScreen.styles';
import { EMAIL_EVENT, EMAIL_IDENTIFIER, IGeneralCallMeEmailTemplate } from '../../common/interface/IPayload';

interface IState {
    name: ITextFieldData;
    phone: ITextFieldData;
    email: ITextFieldData;
}

const defaults = (val: string): ITextFieldData => {
    return { value: val, status: 'valid', event: 'none' };
};

const CallMeScreenView: FunctionComponent<IAppProps> = (props): JSX.Element => {
    const common = props.config.resourceBundles['dk.onsa-common'].read();
    const cb_c = props.config.contentBlocks['claimsrmo'].read();
    const cb_rgf = props.config.contentBlocks['common/rmo-generel-fejlmeddelelse'].read();
    const { emailEvent, hasError, claimHistory, cvrData, quote } = useGlobalState();
    const appDispatch = useGlobalDispatch();
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [state, setState] = useState<IState>({
        name: defaults(''),
        phone: defaults(''),
        email: defaults(''),
    });
    const classes = useStyles();
    const baseClasses = baseStyles();
    const markers: Array<any> = [{ key: '{{insurance}}', value: 'Erhvervsforsikring' }];

    const fieldEventHandler = (data: ITextFieldData, property: string) => {
        setSubmitted(false);
        setState((pState) => ({ ...pState, [property]: data }));
    };

    function submitEventHandler(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setSubmitted(false);

        if (
            state.name.status === 'valid' &&
            state.name.value !== '' &&
            state.phone.status === 'valid' &&
            state.phone.value !== '' &&
            (claimHistory.terminationTerms !== 'Ja' || (state.email.status === 'valid' && state.email.value !== ''))
        ) {
            const tracking = [
                { id: 'name', value: state.name.value },
                { id: 'phone', value: state.phone.value },
            ];
            if (
                emailEvent === EMAIL_EVENT.STRICTER_CONDITIONS ||
                emailEvent === EMAIL_EVENT.BUSINESS_LINE_CLEANER ||
                emailEvent === EMAIL_EVENT.BUSINESS_LINE_CARPENTER
            ) {
                tracking.push({ id: 'email', value: state.email.value });
                appDispatch({ type: REDUCER_KEY.SET_STATE_CHANGE, payload: true });
            }

            track.form('RMO-form', tracking);

            let emailPayload: IGeneralCallMeEmailTemplate = {
                identifier: EMAIL_IDENTIFIER.GENERAL_CALLME,
                event: emailEvent || EMAIL_EVENT.GENERAL_ERROR,
                phoneNumber: state.phone.value,
                fullName: state.name.value,
                email: state.email.value,
            };
            if (cvrData.cvrNumber) {
                emailPayload = {
                    ...emailPayload,
                    industryCode: cvrData.business.key,
                    industryName: cvrData.business.name,
                    companyName: cvrData.name,
                    address: cvrData.address,
                    zipCity: cvrData.zipcodeAndCity,
                    cvrNumber: cvrData.cvrNumber,
                    employees: str2float(cvrData.employeesStr),
                };
            }

            if (emailEvent === EMAIL_EVENT.GENERAL_ERROR) {
                setSending(true);
                sendEmail(props, appDispatch, emailPayload)
                    .then(() => {
                        setSending(false);
                        if (hasError.separateCallMe) {
                            appDispatch({ type: REDUCER_KEY.EMAIL_SENT, payload: true });
                            appDispatch({
                                type: REDUCER_KEY.SET_ERROR,
                                payload: { stepOne: false, stepTwo: true, separateCallMe: true },
                            });
                            appDispatch({ type: REDUCER_KEY.SET_FLOW, payload: FLOW.QUOTE });
                        } else {
                            appDispatch({
                                type: REDUCER_KEY.SET_ERROR,
                                payload: { stepOne: false, stepTwo: true, separateCallMe: false },
                            });
                            appDispatch({ type: REDUCER_KEY.EMAIL_SENT, payload: true });
                        }
                    })
                    .catch((err) => {
                        props.c9log.error(`Error calling send email ${err}`, 'Call me screen');
                        track.fault(`send email in RMO ${err}`, errorTypes.tech, 'RMO form');
                    });
            } else {
                appDispatch({
                    type: REDUCER_KEY.QUOTE_INFO,
                    payload: {
                        ...quote,
                        name: state.name.value,
                        email: state.email.value,
                        phone: state.phone.value,
                        condition: emailEvent,
                    },
                });
                appDispatch({
                    type: REDUCER_KEY.SET_ERROR,
                    payload: { stepOne: false, stepTwo: true, separateCallMe: true },
                });
                appDispatch({ type: REDUCER_KEY.SET_FLOW, payload: FLOW.QUOTE });
            }
        } else {
            track.fault('Invalid or missing RMO field values', errorTypes.user, 'RMO form');
            setSubmitted(true);
        }
    }

    return (
        <GridWrapper showChatIcon={false}>
            <div className={classes.textWrapper}>
                {emailEvent === EMAIL_EVENT.STRICTER_CONDITIONS && <RenderHtml>{cb_c.data}</RenderHtml>}
                {(!emailEvent || emailEvent === EMAIL_EVENT.GENERAL_ERROR) && (
                    <RenderHtml substitutes={markers}>{cb_rgf.data}</RenderHtml>
                )}
            </div>
            <form
                noValidate={true}
                onSubmit={submitEventHandler}
                name={`onsa-b2b-rmo`}
                id={`onsa-b2b-rmo`}
                className={classes.form}
            >
                <TextField
                    id="onsa-b2b-name"
                    required={false}
                    label={common['contact.name']}
                    type="name"
                    fieldEvent={(data: ITextFieldData) => fieldEventHandler(data, 'name')}
                    value={state.name.value}
                    errorTextInvalid={'Indtast navn'}
                    isSubmitted={submitted}
                />
                <TextField
                    id="onsa-b2b-phone"
                    required={false}
                    label={common['contact.phone']}
                    type="phone"
                    fieldEvent={(data: ITextFieldData) => fieldEventHandler(data, 'phone')}
                    value={state.phone.value}
                    errorTextInvalid={'Telefonnummer (8 tal)'}
                    isSubmitted={submitted}
                    className={classes.inputPhone}
                />
                {(claimHistory.terminationTerms === 'Ja' ||
                    emailEvent === EMAIL_EVENT.BUSINESS_LINE_CLEANER ||
                    emailEvent === EMAIL_EVENT.BUSINESS_LINE_CARPENTER) && (
                    <TextField
                        id="onsa-b2b-email"
                        required={false}
                        label={common['contact.email']}
                        type="email"
                        fieldEvent={(data: ITextFieldData) => fieldEventHandler(data, 'email')}
                        value={state.email.value}
                        isSubmitted={submitted}
                        errorTextInvalid={'Mindst ét @ og ét punktum'}
                    />
                )}
                <div className={classes.spinnerContainer}>
                    <Button type={'submit'} fullWidth variant="contained">
                        {common['button.callme']}
                    </Button>
                    {sending && <CircularProgress role="spinner" size={24} className={baseClasses.buttonProgress} />}
                </div>
            </form>
        </GridWrapper>
    );
};

export default CallMeScreenView;
